import React, { useState } from "react";
import { Info, Book, AlertTriangle, Lightbulb, Shield, HelpCircle } from "lucide-react";

export default function InformationHub() {
  const [activeTab, setActiveTab] = useState("Guide Words");
  
  const tabs = [
    { name: "Guide Words", icon: Book },
    { name: "Causes", icon: AlertTriangle },
    { name: "Consequences", icon: Lightbulb },
    { name: "Existing Safeguards", icon: Shield },
    { name: "Recommendation", icon: HelpCircle }
  ];

  const data = {
    "Guide Words": [
      { word: "No/None", description: "Absence of flow, pressure, temperature, etc." },
      { word: "More/Excess", description: "Higher than normal flow, pressure, temperature, etc." },
      { word: "Less/Deficient", description: "Lower than normal flow, pressure, temperature, etc." },
      { word: "As Well As", description: "Additional material, operation, or condition." },
      { word: "Part of", description: "Only part of the intended material or process is present." },
      { word: "Reverse", description: "Flow or operation in the opposite direction." },
      { word: "Other Than", description: "Different material, equipment, or condition than intended." }
    ],
    
    "Causes": [
        { word: "Equipment-Related Causes", description: "Mechanical Failure, Instrumentation Failure, Valve Malfunction, Pump or Motor Failure, Structural Issues." },
        { word: "Human-Related Causes", description: "Operator Error, Lack of Training, Communication Failure, Fatigue or Stress." },
        { word: "Process-Related Causes", description: "Process Design Flaws, Incorrect Setpoints, Overloading, Process Upsets." },
        { word: "External Causes", description: "Environmental Factors, Power Supply Issues, Third-Party Interference." },
        { word: "Operational Causes", description: "Improper Start-up/Shutdown Procedures, Maintenance Errors, Material Handling Errors." },
        { word: "Safety System Failures", description: "Alarm/Interlock Failure, Emergency Response Failures." },
        { word: "Chemical and Physical Causes", description: "Reactivity, Thermal Stress, Corrosive Environments." }
    ],
    "Consequences": [
        { word: "Safety-Related Consequences", description: "Injury or Fatalities, Exposure to Hazardous Substances, Fire or Explosion, Loss of Containment." },
        { word: "Equipment and Infrastructure Consequences", description: "Equipment Damage, Structural Failures, Deformation or Distortion, Fire Damage to Assets." },
        { word: "Environmental Consequences", description: "Chemical Spills or Leaks, Hazardous Waste Generation, Air or Water Pollution, Ecological Damage." },
        { word: "Operational and Process Consequences", description: "Process Shutdown, Product Contamination, Loss of Control, Downtime." },
        { word: "Financial Consequences", description: "Repair and Replacement Costs, Insurance Claims, Legal Liabilities, Revenue Loss." },
        { word: "Reputational Consequences", description: "Loss of Public Trust, Damage to Brand Image, Regulatory Reputation." },
        { word: "Emergency Response Consequences", description: "Emergency Evacuations, Resource Strain, Delayed Response Times." }
    ],
    
    "Existing Safeguards": [
        { word: "Physical Safeguards", description: "Pressure Relief Devices, Containment Systems, Fire Protection Systems, Explosion Protection, Mechanical Barriers, Ventilation Systems." },
        { word: "Process Control Safeguards", description: "Automated Control Systems, Alarms and Interlocks, Process Monitoring, Redundancy." },
        { word: "Operational Safeguards", description: "Standard Operating Procedures (SOPs), Permit-to-Work Systems, Training and Competency, Regular Maintenance." },
        { word: "Human Factors Safeguards", description: "Clear Communication Protocols, Safety Culture, Decision Support Tools." },
        { word: "Emergency Response Safeguards", description: "Emergency Shutdown Systems (ESD), Fire and Gas Detection Systems, Spill Response Kits, Evacuation Plans." },
        { word: "Regulatory and Compliance Safeguards", description: "Safety Audits and Inspections, Regulatory Safety Devices, Risk Assessments." },
        { word: "Financial and Insurance Safeguards", description: "Insurance Coverage, Incident Reporting Systems." }
    ],
    "Recommendation": [
        { word: "Technical Recommendations", description: "Equipment modifications, process control changes, SIS, ESD, material substitution." },
        { word: "Operational Recommendations", description: "SOPs, permit-to-work systems, maintenance practices, operational limits." },
        { word: "Human Factors Recommendations", description: "Training programs, operator interfaces, communication protocols, shift handovers." },
        { word: "Safety and Emergency Response Recommendations", description: "Emergency plans, fire/gas detection, spill containment, incident reporting." },
        { word: "Administrative and Management Recommendations", description: "Safety audits, risk assessments, leadership involvement, hazard identification." },
        { word: "Regulatory and Compliance Recommendations", description: "Compliance with standards, documentation, legal and regulatory reviews." }
    ]
  };
  
  const ActiveIcon = tabs.find(tab => tab.name === activeTab)?.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className=" mb-6">
          <div className="flex items-center gap-3 ">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Info className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Information Hub
            </h1>
          </div>
          <p className="text-slate-600 text-lg ml-12">
            Essential reference for your safety analysis
          </p>
        </div>
        
        {/* Tabs for Navigation */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6 p-2 rounded-2xl bg-white/50 backdrop-blur-sm border border-white/20 shadow-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.name}
                className={`py-2 px-4 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2
                  ${activeTab === tab.name 
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md transform scale-105"
                    : "bg-white text-slate-700 hover:bg-slate-100"
                  }`}
                onClick={() => setActiveTab(tab.name)}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>
        
        {/* Content Table */}
        <div className="relative overflow-hidden mt-8">
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-blue-50/50 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                  {ActiveIcon && <ActiveIcon className="h-6 w-6 text-white" />}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{activeTab}</h2>
                  <p className="text-slate-600 text-sm mt-1">Details and descriptions for selected topic</p>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider w-1/4">
                      {activeTab.split(' ')[0]}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider w-3/4">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {data[activeTab].length > 0 ? (
                    data[activeTab].map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-white/50 transition-all duration-300 group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-800 font-semibold">{item.word}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600">{item.description}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="text-center py-4 text-slate-500">No data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}